import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import {
  getWeeklyPlans,
  createWeeklyPlan,
  updateWeeklyPlan,
  forwardWeeklyPlan,
  updateStatus,
  deletePlan,
  updateExecutiveMembers
} from './api';

const initialState = {
  loading: false,
  createLoading: false,
  pendingLoading: false,
  weeklyPlans: [],
  pendingPlans: [],
  pagination: {
    current_page: 1,
    total_pages: 0,
    total_records: 0,
    total_perpage: 10,
    next_page: null,
    previous_page: null
  }
};

const actions = {
  GET_WEEKLY_PLANS: 'weeklyPlans/GET_WEEKLY_PLANS',
  CREATE_WEEKLY_PLAN: 'weeklyPlans/CREATE_WEEKLY_PLAN',
  UPDATE_WEEKLY_PLAN: 'weeklyPlans/UPDATE_WEEKLY_PLAN',
  FORWARD_WEEKLY_PLAN: 'weeklyPlans/FORWARD_WEEKLY_PLAN',
  UPDATE_STATUS: 'weeklyPlans/UPDATE_STATUS',
  DELETE_PLAN: 'weeklyPlans/DELETE_WEEKLY_PLAN',
  UPDATE_EXECUTIVE_MEMBER: 'weeklyPlans/UPDATE_EXECUTIVE_MEMBER'
};

// ✅ Fetch Weekly Plans
export const fetchWeeklyPlans = createAsyncThunk(
  actions.GET_WEEKLY_PLANS,
  async (payload) => {
    const response = await getWeeklyPlans('get_weekly_plans', payload);
    console.log('Weekly Plans Response:', response);
    return response;
  }
);

export const createWeeklyPlansAsync = createAsyncThunk(
  actions.CREATE_WEEKLY_PLAN,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createWeeklyPlan(payload);
      console.log('CREATE API RESPONSE:', response);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response || error);
    }
  }
);

export const updateWeeklyPlansAsync = createAsyncThunk(
  actions.UPDATE_WEEKLY_PLAN,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateWeeklyPlan(payload);
      console.log('UPDATE API RESPONSE:', response);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response || error);
    }
  }
);

export const forwardWeeklyPlansAsync = createAsyncThunk(
  actions.FORWARD_WEEKLY_PLAN,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await forwardWeeklyPlan(payload);
      console.log('FORWARD API RESPONSE:', response);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response || error);
    }
  }
);

export const updateWeeklyPlanStatusAsync = createAsyncThunk(
  actions.UPDATE_STATUS,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateStatus(payload);
      console.log('UPDATE STATUS RESPONSE:', response);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response || error);
    }
  }
);

export const deleteWeeklyPlanAsync = createAsyncThunk(
  actions.DELETE_PLAN,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deletePlan(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response || error);
    }
  }
);

export const updateExecutiveMemberAsync = createAsyncThunk(
  actions.UPDATE_EXECUTIVE_MEMBER,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateExecutiveMembers(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response || error);
    }
  }
);

export const weeklyPlansSlice = createSlice({
  name: 'weeklyPlans',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ================= GET WEEKLY PLANS =================
      // ================= GET WEEKLY PLANS =================
      .addCase(fetchWeeklyPlans.pending, (state, action) => {
        const isPendingCall = action.meta.arg?.status === 'ACTIVE';

        if (isPendingCall) {
          state.pendingLoading = true;
        } else {
          state.loading = true;
        }
      })
      .addCase(fetchWeeklyPlans.fulfilled, (state, action) => {
        const isPendingCall = action.meta.arg?.status === 'ACTIVE';

        const { success, message: msg, data, pagination } = action.payload;

        if (!success) {
          message.error(msg || 'Failed to fetch weekly plans');
          state.loading = false;
          state.pendingLoading = false;
          return;
        }

        if (isPendingCall) {
          // ✅ Pending tasks (ACTIVE)
          state.pendingLoading = false;
          state.pendingPlans = data || [];
        } else {
          // ✅ Weekly range tasks
          state.loading = false;
          state.weeklyPlans = data || [];
          state.pagination = pagination || initialState.pagination;
        }
      })
      .addCase(fetchWeeklyPlans.rejected, (state, action) => {
        state.loading = false;
        state.pendingLoading = false;

        message.error(
          action.error?.message ||
            'Something went wrong while fetching weekly plans'
        );
      })

      // ================= CREATE WEEKLY PLAN =================
      .addCase(createWeeklyPlansAsync.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(createWeeklyPlansAsync.fulfilled, (state, action) => {
        state.createLoading = false;

        const { success, message: msg, data } = action.payload;

        if (success) {
          message.success(msg || 'Weekly plan created successfully');

          // Option 1: Push to existing list (quick UX)
          state.weeklyPlans.unshift(data);

          // Option 2 (recommended):
          // After create → re-fetch weekly plans from backend
        } else {
          message.error(msg || 'Failed to create weekly plan');
        }
      })
      .addCase(createWeeklyPlansAsync.rejected, (state, action) => {
        state.createLoading = false;
        message.error(
          action.payload?.message ||
            'Something went wrong while creating weekly plan'
        );
      })

      // ================= UPDATE WEEKLY PLAN =================
      .addCase(updateWeeklyPlansAsync.pending, (state) => {
        state.createLoading = true; // reuse loader
      })
      .addCase(updateWeeklyPlansAsync.fulfilled, (state, action) => {
        state.createLoading = false;

        const { success, message: msg, data } = action.payload;

        if (success) {
          message.success(msg || 'Weekly plan updated successfully');

          // Update item in list (instant UI update)
          const index = state.weeklyPlans.findIndex(
            (item) => item.id === data.id
          );

          if (index !== -1) {
            state.weeklyPlans[index] = data;
          }
        } else {
          message.error(msg || 'Failed to update weekly plan');
        }
      })
      .addCase(updateWeeklyPlansAsync.rejected, (state, action) => {
        state.createLoading = false;
        message.error(
          action.payload?.message ||
            'Something went wrong while updating weekly plan'
        );
      })
      // ================= FORWARD WEEKLY PLAN =================
      .addCase(forwardWeeklyPlansAsync.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(forwardWeeklyPlansAsync.fulfilled, (state, action) => {
        state.createLoading = false;

        const { success, message: msg, data } = action.payload;

        if (success) {
          message.success(msg || 'Weekly plan forwarded successfully');

          // Optional: update list if API returns updated record
          const index = state.weeklyPlans.findIndex(
            (item) => item.id === data?.id
          );

          if (index !== -1) {
            state.weeklyPlans[index] = data;
          }
        } else {
          message.error(msg || 'Failed to forward weekly plan');
        }
      })
      .addCase(forwardWeeklyPlansAsync.rejected, (state, action) => {
        state.createLoading = false;
        message.error(
          action.payload?.message ||
            'Something went wrong while forwarding weekly plan'
        );
      })

      // ================= UPDATE WEEKLY PLAN STATUS =================
      .addCase(updateWeeklyPlanStatusAsync.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(updateWeeklyPlanStatusAsync.fulfilled, (state, action) => {
        state.createLoading = false;

        const { success, message: msg, data } = action.payload;

        if (success) {
          message.success(msg || 'Status updated successfully');

          // update weekly list
          const index = state.weeklyPlans.findIndex(
            (item) => item.id === data?.id
          );

          if (index !== -1) {
            state.weeklyPlans[index].status = data.status;
          }

          // ✅ remove from pending if completed
          if (data.status !== 'ACTIVE') {
            state.pendingPlans = state.pendingPlans.filter(
              (item) => item.id !== data.id
            );
          }
        } else {
          message.error(msg || 'Failed to update status');
        }
      })

      .addCase(updateWeeklyPlanStatusAsync.rejected, (state, action) => {
        state.createLoading = false;
        message.error(
          action.payload?.message ||
            'Something went wrong while updating status'
        );
      })

      .addCase(deleteWeeklyPlanAsync.pending, (state) => {
        state.createLoading = true; // reuse loader
      })
      .addCase(deleteWeeklyPlanAsync.fulfilled, (state, action) => {
        state.createLoading = false;
        const { success, message: msg, data } = action.payload;

        if (success) {
          message.success(msg || 'Weekly plan deleted successfully');
          // state.weeklyPlans = state.weeklyPlans.filter(
          //   (item) => item.id !== data.id
          // );
          // state.pendingPlans = state.pendingPlans.filter(
          //   (item) => item.id !== data.id
          // );
        } else {
          message.error(msg || 'Failed to delete weekly plan');
        }
      })
      .addCase(deleteWeeklyPlanAsync.rejected, (state, action) => {
        state.createLoading = false;
        message.error(
          action.payload?.message ||
            'Something went wrong while deleting weekly plan'
        );
      })

      // ================= UPDATE EXECUTIVE MEMBER =================
      .addCase(updateExecutiveMemberAsync.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(updateExecutiveMemberAsync.fulfilled, (state, action) => {
        state.createLoading = false;

        const { success, message: msg, data } = action.payload;

        if (success) {
          message.success(msg || 'Executive member updated successfully');

          const index = state.weeklyPlans.findIndex(
            (item) => item.id === data?.id
          );

          if (index !== -1) {
            state.weeklyPlans[index] = { ...state.weeklyPlans[index], ...data };
          }
        } else {
          message.error(msg || 'Failed to update executive member');
        }
      })
      .addCase(updateExecutiveMemberAsync.rejected, (state, action) => {
        state.createLoading = false;
        message.error(
          action.payload?.message ||
            'Something went wrong while updating executive member'
        );
      });
  }
});

export const selectWeeklyPlansSlice = (state) => state.weeklyPlans;
export default weeklyPlansSlice.reducer;
